import type { ToolId } from '../layout/workspace-config';

export type ReportAction = 'print' | 'csv';

export type ReportId =
  | 'scenes'
  | 'props'
  | 'cast'
  | 'conflicts'
  | 'breakdown'
  | 'schedule'
  | 'callsheet'
  | 'signin'
  | 'blocking'
  | 'costumebible'
  | 'quickchange'
  | 'deadlines'
  | 'micplot'
  | 'lxcuelist'
  | 'followspot'
  | 'callingscript'
  | 'cuesynopsis'
  | 'sound-cues'
  | 'actor-rehearsal-hours';

export interface ReportOption {
  id: ReportId;
  label: string;
  icon: string;
  description: string;
  actions: ReportAction[];
}

export const REPORT_OPTIONS: ReportOption[] = [
  { id: 'scenes', label: 'Run Sheet', icon: 'table_rows', description: 'Scene-by-scene run sheet', actions: ['print', 'csv'] },
  { id: 'props', label: 'Props List', icon: 'category', description: 'Props organized by scene', actions: ['print', 'csv'] },
  { id: 'cast', label: 'Cast List', icon: 'groups', description: 'Cast and character assignments', actions: ['print', 'csv'] },
  { id: 'conflicts', label: 'Conflict Report', icon: 'event_busy', description: 'Actor conflict dates by performer', actions: ['print'] },
  { id: 'breakdown', label: 'Scene Breakdown', icon: 'view_list', description: 'Characters per scene matrix', actions: ['print'] },
  { id: 'schedule', label: 'Schedule', icon: 'calendar_month', description: 'Rehearsal schedule list or calendar', actions: ['print', 'csv'] },
  { id: 'callsheet', label: 'Call Sheet', icon: 'assignment', description: 'Daily call times for cast', actions: ['print'] },
  { id: 'signin', label: 'Sign-In Sheet', icon: 'how_to_reg', description: 'Attendance tracker', actions: ['print'] },
  { id: 'blocking', label: 'Blocking Charts', icon: 'crop_free', description: 'Stage blocking diagrams', actions: ['print'] },
  { id: 'costumebible', label: 'Costume Bible', icon: 'checkroom', description: 'Complete costume documentation', actions: ['print', 'csv'] },
  { id: 'quickchange', label: 'Quick Change Plot', icon: 'bolt', description: 'Quick change assignments', actions: ['print'] },
  { id: 'deadlines', label: 'Production Deadlines', icon: 'event', description: 'Production deadline report', actions: ['print', 'csv'] },
  { id: 'micplot', label: 'Mic Plot', icon: 'settings_input_antenna', description: 'Mic pack assignments and swaps', actions: ['print'] },
  { id: 'lxcuelist', label: 'LX Cue List', icon: 'tungsten', description: 'Lighting cue list by scene', actions: ['print', 'csv'] },
  { id: 'followspot', label: 'Follow Spot Sheet', icon: 'highlight', description: 'Follow spot operator cue sheet', actions: ['print', 'csv'] },
  { id: 'callingscript', label: 'Calling Script', icon: 'menu_book', description: 'Script with placed cue markers', actions: ['print'] },
  { id: 'cuesynopsis', label: 'Cue Synopsis', icon: 'summarize', description: 'Condensed cue list in show order', actions: ['print'] },
  { id: 'sound-cues', label: 'Sound Cues', icon: 'graphic_eq', description: 'Sound cue export', actions: ['csv'] },
  { id: 'actor-rehearsal-hours', label: 'Actor Rehearsal Hours', icon: 'assignment', description: 'Called rehearsal hours by actor and attendance status', actions: ['print', 'csv'] },
];

export const TOOL_REPORTS: Partial<Record<ToolId, ReportId[]>> = {
  scenes: ['scenes'],
  'show-structure': ['breakdown'],
  props: ['props'],
  personnel: ['cast', 'conflicts'],
  scheduler: ['schedule', 'callsheet', 'signin', 'conflicts'],
  calendar: ['schedule'],
  blocking: ['blocking'],
  costumes: ['costumebible', 'quickchange'],
  production: ['deadlines', 'actor-rehearsal-hours'],
  sound: ['sound-cues'],
  lighting: ['lxcuelist', 'followspot'],
  'prompt-book': ['callingscript', 'cuesynopsis'],
};

const REPORT_OPTION_BY_ID = new Map(REPORT_OPTIONS.map((option) => [option.id, option]));

export function getReportOption(reportId: ReportId): ReportOption {
  const option = REPORT_OPTION_BY_ID.get(reportId);
  if (!option) {
    throw new Error(`Unknown report id: ${reportId}`);
  }
  return option;
}

export function getReportsForTool(toolId: ToolId): ReportOption[] {
  return (TOOL_REPORTS[toolId] ?? []).map(getReportOption);
}

export function getDefaultReportId(toolId: ToolId): ReportId | undefined {
  return TOOL_REPORTS[toolId]?.[0];
}
